import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { 
  Card, 
  CardHeader,
  CardBody, 
  Button, 
  OutlineButton, 
  Badge, 
  TableContainer, 
  Table, 
  TableHead, 
  TableRow, 
  TableCell, 
  TableHeader,
  FlexContainer,
  Input,
  Select,
  Pagination,
  PageButton,
  Grid
} from '../../styles/StyledComponents';
import { useAuth } from '../../context/AuthContext';
import { ProjectFinancialSummary, ProjectCashFlow } from '../../types';
import apiService from '../../services/api';

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
`;

const FilterSection = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 1.5rem;
  background-color: white;
  padding: 1rem;
  border-radius: var(--border-radius);
  box-shadow: var(--box-shadow);
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const FilterGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  label {
    font-weight: 500;
    color: var(--dark-gray);
    white-space: nowrap;
  }
`;

const SearchContainer = styled.div`
  position: relative;
  flex: 1;
  max-width: 300px;
  
  @media (max-width: 768px) {
    max-width: 100%;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 0.5rem;
  
  @media (max-width: 768px) {
    flex-wrap: wrap;
  }
`;

const StatusBadge = styled(Badge)`
  min-width: 80px;
  text-align: center;
`;

const AmountText = styled.span`
  font-weight: 500;
`;

const DateText = styled.span`
  color: var(--dark-gray);
  font-size: 0.85rem;
`;

const NoDataMessage = styled.div`
  text-align: center;
  padding: 2rem;
  color: var(--dark-gray);
`;

const SummaryCard = styled(Card)`
  margin-bottom: 1rem;
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const SummaryItem = styled.div`
  text-align: center;
  padding: 1rem;
  background: white;
  border-radius: var(--border-radius);
  
  .label {
    font-size: 0.9rem;
    color: var(--dark-gray);
    margin-bottom: 0.5rem;
  }
  
  .value {
    font-size: 1.5rem;
    font-weight: 600;
    color: var(--primary-color);
  }
  
  .positive {
    color: var(--success-color);
  }
  
  .negative {
    color: var(--danger-color);
  }
`;

const VarianceIndicator = styled.span<{ variance: number }>`
  color: ${props => props.variance >= 0 ? 'var(--success-color)' : 'var(--danger-color)'};
  font-weight: 600;
`;

const AccountingList: React.FC = () => {
  const { user } = useAuth();
  const [financialSummaries, setFinancialSummaries] = useState<ProjectFinancialSummary[]>([]);
  const [filteredSummaries, setFilteredSummaries] = useState<ProjectFinancialSummary[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [projectFilter, setProjectFilter] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const itemsPerPage = 10;

  useEffect(() => {
    const loadAccountingData = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        // Load projects for financial summaries
        const projectsResponse = await apiService.getProjects();
        const projects = projectsResponse.data;
        
        // For admin users (MD, TD, Accountant), show all projects
        // For other users, show only assigned projects
        const isAdmin = user?.role === 'MD' || user?.role === 'TD' || user?.role === 'Accountant';
        
        const userProjects = isAdmin 
          ? projects // Show all projects for admin users
          : projects.filter((project: any) => 
              project.assignedStaff?.some((staff: any) => staff.userId === user?.id) || 
              project.manager === user?.name
            );
        
        const summaries: ProjectFinancialSummary[] = [];
        
        // Load real financial data for each project
        for (const project of userProjects) {
          try {
            // Fetch real payments for income
            const paymentsResponse = await apiService.getPayments({ projectId: project._id || project.id });
            const payments = paymentsResponse.data || [];
            const income = payments.reduce((sum: number, payment: any) => sum + payment.amount, 0);
            
            // Fetch real expenses
            const expensesResponse = await apiService.getExpenses({ projectId: project._id || project.id });
            const expenses = expensesResponse.data || [];
            const totalExpenses = expenses.reduce((sum: number, expense: any) => sum + expense.amount, 0);
            
            const budget = project.budget.total;
            const balance = income - totalExpenses;
            const variance = budget - totalExpenses;
            
            summaries.push({
              projectId: project._id || project.id || '',
              projectName: project.name,
              budget: {
                total: budget,
                materials: project.budget.materials,
                labor: project.budget.labor,
                equipment: project.budget.equipment,
                subContracts: project.budget.subContracts,
                others: project.budget.others
              },
              actual: {
                income,
                expenses: totalExpenses,
                balance
              },
              variance: {
                budgetVsActual: variance,
                percentage: budget > 0 ? (variance / budget) * 100 : 0
              },
              currency: project.budget.currency,
              lastUpdated: new Date()
            });
          } catch (error) {
            console.error(`Error loading financial data for project ${project.name}:`, error);
            // Fallback to mock data if API fails
            const budget = project.budget.total;
            const income = Math.random() * budget * 0.8;
            const expenses = Math.random() * budget * 0.6;
            const balance = income - expenses;
            const variance = budget - expenses;
            
            summaries.push({
              projectId: project._id || project.id || '',
              projectName: project.name,
              budget: {
                total: budget,
                materials: project.budget.materials,
                labor: project.budget.labor,
                equipment: project.budget.equipment,
                subContracts: project.budget.subContracts,
                others: project.budget.others
              },
              actual: {
                income,
                expenses,
                balance
              },
              variance: {
                budgetVsActual: variance,
                percentage: (variance / budget) * 100
              },
              currency: project.budget.currency,
              lastUpdated: new Date()
            });
          }
        }
        
        setFinancialSummaries(summaries);
        setFilteredSummaries(summaries);
      } catch (error: any) {
        console.error('Error loading accounting data:', error);
        setError('Failed to load accounting data');
      } finally {
        setIsLoading(false);
      }
    };
    
    if (user) {
      loadAccountingData();
    }
  }, [user]);

  useEffect(() => {
    let result = [...financialSummaries];
    
    // Apply search filter
    if (searchTerm) {
      result = result.filter(
        summary => 
          summary.projectName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          summary.projectId.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Apply project filter
    if (projectFilter) {
      result = result.filter(summary => summary.projectId === projectFilter);
    }
    
    // Default sort by last updated (newest first)
    result.sort((a, b) => new Date(b.lastUpdated).getTime() - new Date(a.lastUpdated).getTime());
    
    setFilteredSummaries(result);
  }, [financialSummaries, searchTerm, projectFilter]);

  // Calculate pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredSummaries.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredSummaries.length / itemsPerPage);

  const paginate = (pageNumber: number) => setCurrentPage(pageNumber);

  const formatCurrency = (amount: number, currency: string = 'TZS') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Calculate overall statistics
  const totalBudget = filteredSummaries.reduce((sum, summary) => sum + summary.budget.total, 0);
  const totalIncome = filteredSummaries.reduce((sum, summary) => sum + summary.actual.income, 0);
  const totalExpenses = filteredSummaries.reduce((sum, summary) => sum + summary.actual.expenses, 0);
  const totalBalance = totalIncome - totalExpenses;

  // Get unique projects for filter
  const uniqueProjects = Array.from(new Set(filteredSummaries.map(summary => summary.projectId)))
    .map(projectId => {
      const summary = filteredSummaries.find(s => s.projectId === projectId);
      return {
        id: projectId,
        name: summary?.projectName || ''
      };
    });

  // Check if current user can manage accounting entries
  const canManageAccounting = user?.role === 'MD' || user?.role === 'TD' || user?.role === 'Accountant';

  return (
    <div>
      <PageHeader>
        <h1 className="page-title">Accounting</h1>
        {canManageAccounting && (
          <ActionButtons>
            <Button as={Link} to="/accounting/payment/new">
              + Add Payment
            </Button>


            <OutlineButton>
              Excel
            </OutlineButton>
            <OutlineButton>
              PDF
            </OutlineButton>
          </ActionButtons>
        )}
      </PageHeader>

      <SummaryGrid>
        <SummaryItem>
          <div className="label">Total Budget</div>
          <div className="value">{formatCurrency(totalBudget)}</div>
        </SummaryItem>
        <SummaryItem>
          <div className="label">Total Income</div>
          <div className="value positive">{formatCurrency(totalIncome)}</div>
        </SummaryItem>
        <SummaryItem>
          <div className="label">Total Expenses</div>
          <div className="value negative">{formatCurrency(totalExpenses)}</div>
        </SummaryItem>
        <SummaryItem>
          <div className="label">Net Balance</div>
          <div className={`value ${totalBalance >= 0 ? 'positive' : 'negative'}`}>
            {formatCurrency(totalBalance)}
          </div>
        </SummaryItem>
      </SummaryGrid>

      <FilterSection>
        <SearchContainer>
          <Input 
            type="text" 
            placeholder="Search projects..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </SearchContainer>

        <FilterGroup>
          <label>Project:</label>
          <Select 
            value={projectFilter} 
            onChange={(e) => setProjectFilter(e.target.value)}
          >
            <option value="">All Projects</option>
            {uniqueProjects.map(project => (
              <option key={project.id} value={project.id}>{project.name}</option>
            ))}
          </Select>
        </FilterGroup>

        <Button onClick={() => {
          setSearchTerm('');
          setProjectFilter('');
        }}>
          Reset Filters
        </Button>
      </FilterSection>

      <Card>
        <CardHeader>
          <h3>Project Financial Summary ({filteredSummaries.length})</h3>
        </CardHeader>
        <CardBody>
          {filteredSummaries.length > 0 ? (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeader>Project</TableHeader>
                    <TableHeader>Budget</TableHeader>
                    <TableHeader>Income</TableHeader>
                    <TableHeader>Expenses</TableHeader>
                    <TableHeader>Balance</TableHeader>
                    <TableHeader>Variance</TableHeader>
                    <TableHeader>Last Updated</TableHeader>
                    <TableHeader>Actions</TableHeader>
                  </TableRow>
                </TableHead>
                <tbody>
                  {currentItems.map((summary) => (
                    <TableRow key={summary.projectId}>
                      <TableCell>
                        <Link to={`/projects/${summary.projectId}`}>
                          {summary.projectName}
                        </Link>
                      </TableCell>
                      <TableCell>
                        <AmountText>
                          {formatCurrency(summary.budget.total, summary.currency)}
                        </AmountText>
                      </TableCell>
                      <TableCell>
                        <AmountText style={{ color: 'var(--success-color)' }}>
                          {formatCurrency(summary.actual.income, summary.currency)}
                        </AmountText>
                      </TableCell>
                      <TableCell>
                        <AmountText style={{ color: 'var(--danger-color)' }}>
                          {formatCurrency(summary.actual.expenses, summary.currency)}
                        </AmountText>
                      </TableCell>
                      <TableCell>
                        <AmountText style={{ 
                          color: summary.actual.balance >= 0 ? 'var(--success-color)' : 'var(--danger-color)' 
                        }}>
                          {formatCurrency(summary.actual.balance, summary.currency)}
                        </AmountText>
                      </TableCell>
                      <TableCell>
                        <VarianceIndicator variance={summary.variance.budgetVsActual}>
                          {formatCurrency(summary.variance.budgetVsActual, summary.currency)}
                          <br />
                          <small>({summary.variance.percentage.toFixed(1)}%)</small>
                        </VarianceIndicator>
                      </TableCell>
                      <TableCell>
                        <DateText>{formatDate(summary.lastUpdated)}</DateText>
                      </TableCell>
                                            <TableCell>
                        <FlexContainer gap="0.5rem">
                          <Link to={`/accounting/project/${summary.projectId}`}>
                            <OutlineButton>
                              View Details
                            </OutlineButton>
                          </Link>
                          {/* Only show Add Payment button for MD, TD, and Accountant roles */}
                          {canManageAccounting && user?.role !== 'Project Manager' && (
                            <Link to={`/accounting/payment/new?projectId=${summary.projectId}`}>
                              <Button>
                                Add Payment
                              </Button>
                            </Link>
                          )}
                        </FlexContainer>
                      </TableCell>
                    </TableRow>
                  ))}
                </tbody>
              </Table>
            </TableContainer>
          ) : (
            <NoDataMessage>
              <div className="empty-icon">💰</div>
              <p>No financial data found.</p>
            </NoDataMessage>
          )}
        </CardBody>
      </Card>

      {totalPages > 1 && (
        <Pagination>
          <PageButton 
            onClick={() => paginate(currentPage - 1)}
            disabled={currentPage === 1}
          >
            Previous
          </PageButton>
          
          {Array.from({ length: totalPages }, (_, i) => i + 1).map(number => (
            <PageButton
              key={number}
              onClick={() => paginate(number)}
              active={currentPage === number}
            >
              {number}
            </PageButton>
          ))}
          
          <PageButton 
            onClick={() => paginate(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            Next
          </PageButton>
        </Pagination>
      )}
      
      <div style={{ textAlign: 'right', padding: '1rem 0', color: 'var(--dark-gray)', fontSize: '0.8rem' }}>
        Developed by Sandfish
      </div>
    </div>
  );
};

export default AccountingList; 